import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { articlesForCountry } from "@/lib/article-index.generated";
import { loadArticle, type FullArticle } from "@/lib/article-loader";
import { ArticleSidebar } from "@/components/site/Sidebar";

import { ShareBar } from "@/components/site/ShareBar";
import { RelatedArticles } from "@/components/site/RelatedArticles";
import { autoLinkText } from "@/lib/auto-link";
import { roundFlagUrl } from "@/data/countries";
import { Clock, Calendar, ChevronLeft } from "lucide-react";
import { Breadcrumbs, buildBreadcrumbLd } from "@/components/site/Breadcrumbs";
import { TableOfContents } from "@/components/site/TableOfContents";
import { KeyTakeaways } from "@/components/site/KeyTakeaways";
import { ArticleEndMatter } from "@/components/site/ArticleEndMatter";
import { EditorNote } from "@/components/site/EditorNote";
import { summaryPlacement, notePlacement } from "@/lib/article-variation";
import { buildArticleLd, buildFaqLd, ensureFaq } from "@/lib/article-schema";
import { buildToc } from "@/lib/article-toc";
import { slugify } from "@/lib/slugify";
import { makeSeoTitle } from "@/lib/seo-title";
import { socialImageMeta } from "@/lib/social";
import { makeSeoDescription } from "@/lib/seo-description";

const VALID = new Set(articlesForCountry("italy").map((a) => a.slug));

export const Route = createFileRoute("/italy/$slug")({
  beforeLoad: ({ params }) => {
    if (!VALID.has(params.slug)) throw notFound();
  },
  loader: async ({ params }) => {
    const a = await loadArticle("italy", params.slug);
    if (!a) throw notFound();
    return a;
  },
  head: ({ loaderData }: { loaderData?: FullArticle }) => {
    const a = loaderData;
    if (!a) return {};
    const url = `https://lifewestway.com/italy/${a.slug}`;
    const seoTitle = makeSeoTitle(a.seoTitle ?? a.title);
    return {
      meta: [
        { title: seoTitle },
        { name: "description", content: makeSeoDescription(a.description) },
        { property: "og:title", content: seoTitle },
        { property: "og:description", content: makeSeoDescription(a.description) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...socialImageMeta({ image: a.heroImage, alt: a.title }),
        { property: "og:site_name", content: "LifeWestWay" },
        { property: "article:published_time", content: a.publishedAt },
        { property: "article:modified_time", content: a.updatedAt },
        { property: "article:section", content: a.category },
        { name: "twitter:title", content: seoTitle },
        { name: "twitter:description", content: makeSeoDescription(a.description) },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildArticleLd({
              url,
              headline: a.title,
              description: a.description,
              image: a.heroImage,
              datePublished: a.publishedAt,
              dateModified: a.updatedAt,
              section: a.category,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbLd([
              { label: "Home", href: "/" },
              { label: "Italy", href: "/italy" },
              { label: a.title, href: `/italy/${a.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqLd(ensureFaq(a.faq, { title: a.title, country: "italy", category: a.category, updatedAt: a.updatedAt }), url)),
        },
      ].filter(Boolean) as { type: string; children: string }[],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-bold text-foreground">Article not found</h1>
      <p className="mt-2 text-muted-foreground">This Italy article does not exist yet.</p>
      <div className="mt-6">
        <Link title="Back to the Italy immigration, jobs and tax guide" to="/$country" params={{ country: "italy" }} className="text-accent underline">
          Back to Italy guide
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-foreground">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      <button
        onClick={reset}
        className="mt-6 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white"
      >
        Try again
      </button>
    </div>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  const { slug } = Route.useParams();
  const a = Route.useLoaderData() as FullArticle;

  return (
    <>
      <section className="relative isolate overflow-hidden bg-navy text-white">
        <SafeImage width={1200} height={675}
          eager
          src={a.heroImage}
          alt={`${a.title} — Italy immigration and expat guide`}
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Link title="Back to the Italy country guide"
            to="/$country"
            params={{ country: "italy" }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-accent hover:underline"
          >
            <ChevronLeft className="h-3 w-3" aria-hidden /> Italy
          </Link>
          <div className="mt-4 flex items-center gap-3">
            <SafeImage
              src={roundFlagUrl("it")}
              alt="Italy flag"
              width={40}
              height={40}
              className="h-10 w-10 rounded-full ring-2 ring-accent"
            />
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
              {a.category}
            </span>
          </div>
          <h1 className="mt-4 max-w-4xl font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {a.title}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/80 sm:text-lg">{a.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" aria-hidden /> Updated {a.updatedAt}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
            </span>
          </div>
        </div>
      </section>

      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Italy", href: "/italy" },
          { label: a.title },
        ]}
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        

        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <article className="prose-article lg:col-span-8">
            <TableOfContents items={buildToc(a.sections)} />
            {summaryPlacement(a.slug) === "beforeIntro" ? (
              <>
                <KeyTakeaways slug={a.slug} intro={a.intro} sections={a.sections} faq={a.faq} />
                <p className="lead text-lg leading-relaxed text-foreground">{a.intro}</p>
              </>
            ) : (
              <>
                <p className="lead text-lg leading-relaxed text-foreground">{a.intro}</p>
                <KeyTakeaways slug={a.slug} intro={a.intro} sections={a.sections} faq={a.faq} />
              </>
            )}
            {notePlacement(a.slug) === "afterSummary" ? <EditorNote slug={a.slug} href={`/italy/${a.slug}`} category={a.category} country="italy" countryLabel="Italy" /> : null}

            {a.sections.map((s, i) => {
              if (s.type === "h2") return <h2 key={i} id={slugify(s.text ?? "")}>{s.text}</h2>;
              if (s.type === "h3") return <h3 key={i} id={slugify(s.text ?? "")}>{s.text}</h3>;
              if (s.type === "h4") return <h4 key={i}>{s.text}</h4>;
              if (s.type === "p") return <p key={i}>{autoLinkText(s.text ?? "", `/italy/${slug}`)}</p>;
              if (s.type === "ul")
                return (
                  <ul key={i}>
                    {s.items?.map((it, j) => <li key={j}>{it}</li>)}
                  </ul>
                );
              if (s.type === "ol")
                return (
                  <ol key={i}>
                    {s.items?.map((it, j) => <li key={j}>{it}</li>)}
                  </ol>
                );
              return null;
            })}

            {notePlacement(a.slug) === "beforeFaq" ? <EditorNote slug={a.slug} href={`/italy/${a.slug}`} category={a.category} country="italy" countryLabel="Italy" /> : null}
            <ArticleEndMatter slug={a.slug} faq={ensureFaq(a.faq, { title: a.title, country: "italy", category: a.category, updatedAt: a.updatedAt })} />
            <RelatedArticles country="italy" slug={slug} category={a.category} />
          </article>

          <div className="lg:col-span-4">
            <ArticleSidebar activeCountry="italy" />
          </div>
        </div>
      </div>
      <ShareBar title={a.title} />
    </>
  );
}
